import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { Music2 } from "lucide-react";
import { useStoreSelector } from "../../../../src/lib/store";
import { SelectionToolbar } from "../components/SelectionToolbar";
import { SelectionToolsButtons } from "../components/SelectionToolsButtons";
import { getLabActions } from "../labActions";
import { transportStore } from "../store/transportStore";
import { uiStore } from "../store/uiStore";
import {
  PaintController,
  type SelectionToolbarState,
} from "./PaintController";
import type { TrackerGridHandle } from "./TrackerGridHandle";
import { getTrackerGridCallbacks } from "./trackerGridCallbacks";
import { TrackerHeader } from "./TrackerHeader";
import { setTrackerGrid } from "./trackerGridRegistry";

/**
 * React tracker shell: sticky step header in React, track heads + body in PaintController.
 * Registers a TrackerGridHandle for labRuntime. Callbacks come from
 * `setTrackerGridCallbacks` (filled when the runtime starts).
 */
export function TrackerGrid() {
  const hostRef = useRef<HTMLDivElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLTableElement>(null);
  const paintRef = useRef<PaintController | null>(null);
  const armMode = useStoreSelector(transportStore, s => s.armMode);
  const generateNotesOpen = useStoreSelector(uiStore, s => s.generateNotesOpen);
  const [selectionUi, setSelectionUi] = useState<SelectionToolbarState | null>(null);

  useLayoutEffect(() => {
    const host = hostRef.current;
    const wrap = wrapRef.current;
    const table = tableRef.current;
    if (!host || !wrap || !table) {
      return;
    }

    const paint = new PaintController({
      focusHost: host,
      wrap,
      table,
      onChange: clip => getTrackerGridCallbacks().onChange(clip),
      onMuteSoloChange: state => getTrackerGridCallbacks().onMuteSoloChange?.(state),
      onPositionChange: () => getTrackerGridCallbacks().onPositionChange?.(),
      onActivePatternChange: id => getTrackerGridCallbacks().onActivePatternChange?.(id),
      onAuditionNote: (track, midi, on) =>
        getTrackerGridCallbacks().onAuditionNote?.(track, midi, on),
      onSelectionUi: next => {
        setSelectionUi(prev => {
          if (next === null) {
            return prev === null ? prev : null;
          }
          if (
            prev
            && prev.canRamp === next.canRamp
            && prev.canFillTop === next.canFillTop
            && prev.anchor.left === next.anchor.left
            && prev.anchor.top === next.anchor.top
            && prev.anchor.right === next.anchor.right
            && prev.anchor.bottom === next.anchor.bottom
          ) {
            return prev;
          }
          return next;
        });
      },
    });
    paintRef.current = paint;
    paint.setArmMode(transportStore.getValue().armMode);

    const handle: TrackerGridHandle = {
      getClip: () => paint.getClip(),
      setClip: (clip, options) => paint.setClip(clip, options),
      getActivePatternId: () => paint.getActivePatternId(),
      setActivePatternId: id => paint.setActivePatternId(id),
      getViewPatternIds: () => paint.getViewPatternIds(),
      setViewPatternIds: ids => paint.setViewPatternIds(ids),
      locateViewStep: step => paint.locateViewStep(step),
      locateGlobalStep: (patternId, localStep) => paint.locateGlobalStep(patternId, localStep),
      focus: () => paint.focus(),
      getCursorStep: () => paint.getCursorStep(),
      getCursorTrack: () => paint.getCursorTrack(),
      getPlayheadRow: () => paint.getPlayheadRow(),
      jumpToFirstRow: () => paint.jumpToFirstRow(),
      jumpToCell: (step, track, offset, options) =>
        paint.jumpToCell(step, track, offset, options),
      getMuted: () => paint.getMuted(),
      getSolo: () => paint.getSolo(),
      toggleMute: track => paint.toggleMute(track),
      toggleSolo: track => paint.toggleSolo(track),
      setMuteSolo: (muted, solo, options) => paint.setMuteSolo(muted, solo, options),
      renameTrack: (track, name) => paint.renameTrack(track, name),
      checkpoint: () => paint.checkpoint(),
      setPlayheadRow: row => paint.setPlayheadRow(row),
      setFollowPlayhead: on => paint.setFollowPlayhead(on),
      isFollowPlayhead: () => paint.isFollowPlayhead(),
      setArmMode: on => paint.setArmMode(on),
      isArmMode: () => paint.isArmMode(),
      applyRowMarker: (step, marker) => paint.applyRowMarker(step, marker),
      getGenerateNotesTarget: () => paint.getGenerateNotesTarget(),
      applySelectionGeneratedNotes: rowNotes => paint.applySelectionGeneratedNotes(rowNotes),
      setTrackColors: colors => paint.setTrackColors(colors),
      getTrackColors: () => paint.getTrackColors(),
    };
    setTrackerGrid(handle);

    return () => {
      setTrackerGrid(null);
      paint.dispose();
      paintRef.current = null;
    };
  }, []);

  // After React re-renders chrome that owns `<tbody />`, recover the pool.
  useLayoutEffect(() => {
    paintRef.current?.recoverPoolDom();
  }, [selectionUi, armMode]);

  useEffect(() => {
    paintRef.current?.setArmMode(armMode);
  }, [armMode]);

  const runTool = (action: () => boolean | void): void => {
    action();
    paintRef.current?.focus();
  };

  const tools = {
    canFillTop: selectionUi?.canFillTop ?? false,
    canRamp: selectionUi?.canRamp ?? false,
    canRandomHex: selectionUi != null,
    onFillTop: () => runTool(() => paintRef.current?.applySelectionFillTop()),
    onRamp: () => runTool(() => paintRef.current?.applySelectionRamp()),
    onRandomHex: () => runTool(() => paintRef.current?.applySelectionRandomHex()),
  };

  return (
    <div
      ref={hostRef}
      className={`tracker-host${armMode ? " arm-mode" : ""}`}
      tabIndex={0}
    >
      <aside
        className="selection-tools-rail flex shrink-0 flex-col items-center self-stretch rounded-md border border-lab-line bg-lab-panel-bar p-1"
        aria-label="Tracker tools"
      >
        <button
          type="button"
          className={`tb-icon${armMode ? " active arm" : ""}`}
          title={
            armMode
              ? "Arm on (⌘/Ctrl+K · Esc to exit)"
              : "Arm — piano typing writes notes (⌘/Ctrl+K)"
          }
          aria-label="Arm note entry"
          aria-pressed={armMode}
          data-selection-toolbar=""
          onMouseDown={event => {
            event.preventDefault();
            event.stopPropagation();
          }}
          onClick={event => {
            event.preventDefault();
            event.stopPropagation();
            getLabActions()?.toggleArmMode();
          }}
        >
          <svg viewBox="0 0 24 24" width={18} height={18} aria-hidden={true}>
            <circle cx="12" cy="12" r="7" fill="none" stroke="currentColor" strokeWidth="2" />
            <circle cx="12" cy="12" r="3" fill="currentColor" />
          </svg>
        </button>
        <div
          className="my-1 h-px w-7 shrink-0 bg-[color-mix(in_srgb,var(--line)_85%,transparent)]"
          aria-hidden={true}
        />
        <button
          type="button"
          className={`tb-icon${generateNotesOpen ? " active" : ""}`}
          title="Generate notes — fill selection from a chord/arp pattern"
          aria-label="Generate notes"
          aria-pressed={generateNotesOpen}
          data-selection-toolbar=""
          onMouseDown={event => {
            event.preventDefault();
            event.stopPropagation();
          }}
          onClick={event => {
            event.preventDefault();
            event.stopPropagation();
            getLabActions()?.toggleGenerateNotes();
          }}
        >
          <Music2 size={18} strokeWidth={2.25} aria-hidden={true} />
        </button>
        <div
          className="my-1 h-px w-7 shrink-0 bg-[color-mix(in_srgb,var(--line)_85%,transparent)]"
          aria-hidden={true}
        />
        <SelectionToolsButtons orientation="vertical" {...tools} />
      </aside>
      <div className="tracker-grid-column">
        <div ref={wrapRef} className="grid-wrap">
          <table ref={tableRef} className="tracker">
            <thead>
              <TrackerHeader />
            </thead>
            <tbody />
          </table>
        </div>
      </div>
      {selectionUi && (
        <SelectionToolbar
          state={selectionUi}
          onRamp={tools.onRamp}
          onFillTop={tools.onFillTop}
          onRandomHex={tools.onRandomHex}
        />
      )}
    </div>
  );
}
